import { useCallback, useMemo, useState } from 'react';
import {
  ArrowUturnLeftIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  TagIcon,
} from '@heroicons/react/24/outline';
import type { PublishStateFilter } from '@xphoto/shared';
import { useI18n } from '../i18n';
import { interpolate } from '../gallery/format';
import { useFolders } from '../gallery/use-facets';
import type { ExifDeepLink } from '../gallery/state';
import { ApiError } from '../gallery/api';
import { recordAction } from '../diagnostics/buffers';
import { enqueueExifPublish } from './exif-api';
import { useExifAnalysis } from './use-exif-analysis';
import { FolderScope, InlineWarning, Stat, Toggle } from './exif-controls';

/**
 * Écran « PUBLIER DES TAGS VERS EXIF » (nouvel outil issu du SPLIT — v0.53.0).
 *
 * PAGE rendue dans la zone `main` sous l'en-tête persistant (`AppChrome`) — le fil
 * d'Ariane (« Accueil / Publier des tags EXIF ») reste visible et pilote le retour.
 * Défile via son propre conteneur `overflow-y-auto`.
 *
 * Contient UNIQUEMENT la publication (XPHOTO-06) : exporte les tags VALIDÉS en base
 * (IPTC:Keywords + XMP dc:subject) vers les fichiers divergents. Les compteurs
 * Synchronisés / À publier sont CLIQUABLES → filtrent la galerie par état de
 * publication (`publishState`).
 *
 * ÉCRITURE OPT-IN : un 409 `WRITE_DISABLED` (bibliothèque en lecture seule) est
 * intercepté et affiché avec un message dédié (comme le dédoublonnage).
 */
interface ExifPublishPageProps {
  libraryId: string;
  onClose: () => void;
  /** Lien profond depuis un compteur → galerie filtrée (XPHOTO-24). */
  onDeepLink: (link: ExifDeepLink) => void;
}

export function ExifPublishPage({
  libraryId,
  onClose,
  onDeepLink,
}: ExifPublishPageProps): React.JSX.Element {
  const { t } = useI18n();
  const [refreshKey, setRefreshKey] = useState(0);
  // Portée dossier partagée (re-scope l'analyse de publication comme le Nettoyage EXIF).
  const [folder, setFolder] = useState('');

  const analysis = useExifAnalysis(libraryId, refreshKey, folder || undefined);
  const folders = useFolders(libraryId, refreshKey);

  const handleJobQueued = useCallback(() => setRefreshKey((k) => k + 1), []);
  const folderOptions = useMemo(() => (folders.data ?? []).map((f) => f.path), [folders.data]);

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* En-tête de page. */}
      <div className="flex items-center justify-between gap-2 border-b border-border pb-4">
        <h1 className="flex items-center gap-2 text-lg font-semibold text-fg">
          <TagIcon className="h-6 w-6" aria-hidden="true" />
          {t('exif.publishToolTitle')}
        </h1>
        <button
          type="button"
          onClick={onClose}
          title={t('exif.publishToolClose')}
          aria-label={t('exif.publishToolClose')}
          className="rounded-md p-2 text-muted hover:bg-surface-2 hover:text-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
        >
          <ArrowUturnLeftIcon className="h-6 w-6" aria-hidden="true" />
        </button>
      </div>

      {/* Corps défilant (le seul conteneur de défilement de la page). `relative` : ancre
          les descendants `sr-only`/`absolute` à ce conteneur, pas au document (cf. v0.48.0). */}
      <div className="relative min-h-0 flex-1 overflow-y-auto py-4">
        <div className="mx-auto flex max-w-4xl flex-col gap-8">
          <p className="text-sm text-muted">{t('exif.publishToolIntro')}</p>

          {analysis.status === 'error' ? (
            <div className="flex flex-col items-center gap-3 py-12 text-center">
              <ExclamationTriangleIcon className="h-10 w-10 text-danger" aria-hidden="true" />
              <p className="text-fg">{t('exif.errorTitle')}</p>
              <button
                type="button"
                onClick={analysis.reload}
                className="rounded-md bg-brand px-3 py-2 text-sm font-medium text-brand-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
              >
                {t('exif.retry')}
              </button>
            </div>
          ) : analysis.status === 'ready' && analysis.data ? (
            <PublishPanel
              libraryId={libraryId}
              publish={analysis.data.publish}
              folder={folder}
              onFolderChange={setFolder}
              folderOptions={folderOptions}
              onJobQueued={handleJobQueued}
              onSelectPublishState={(state) =>
                onDeepLink({ kind: 'publish', state, ...(folder ? { folder } : {}) })
              }
            />
          ) : (
            <p className="py-12 text-center text-muted" role="status">
              {t('exif.loading')}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

/** Panneau PUBLICATION (XPHOTO-06) : divergence DB↔fichier + action Publier. */
function PublishPanel({
  libraryId,
  publish,
  folder,
  onFolderChange,
  folderOptions,
  onJobQueued,
  onSelectPublishState,
}: {
  libraryId: string;
  publish: { taggedAssets: number; inSync: number; outOfSync: number };
  folder: string;
  onFolderChange: (folder: string) => void;
  folderOptions: readonly string[];
  onJobQueued: () => void;
  onSelectPublishState: (state: PublishStateFilter) => void;
}): React.JSX.Element {
  const { t } = useI18n();
  // Décoché par défaut (opt-in) : évite d'accumuler des `_original` inaperçus.
  const [keepOriginal, setKeepOriginal] = useState(false);
  const [dryRun, setDryRun] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<'generic' | 'writeDisabled' | null>(null);
  const [queuedJobId, setQueuedJobId] = useState<string | null>(null);

  const submit = async (): Promise<void> => {
    if (busy) return;
    setBusy(true);
    setError(null);
    setQueuedJobId(null);
    try {
      const job = await enqueueExifPublish(libraryId, {
        ...(folder ? { folder } : {}),
        keepOriginal,
        dryRun,
      });
      recordAction('exif.publish', { dryRun: dryRun ? 1 : 0 });
      setQueuedJobId(job.id);
      onJobQueued();
    } catch (err) {
      // 409 WRITE_DISABLED : bibliothèque en lecture seule → message dédié.
      if (err instanceof ApiError && err.status === 409 && err.code === 'WRITE_DISABLED') {
        setError('writeDisabled');
      } else {
        setError('generic');
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      aria-labelledby="exif-publish-heading"
      className="flex flex-col gap-4 rounded-lg border border-border p-4"
    >
      <div className="flex flex-col gap-1">
        <h3
          id="exif-publish-heading"
          className="flex items-center gap-2 text-base font-semibold text-fg"
        >
          <TagIcon className="h-5 w-5" aria-hidden="true" />
          {t('exif.publishTitle')}
        </h3>
        <p className="text-sm text-muted">{t('exif.publishHint')}</p>
      </div>

      {/* Divergence DB ↔ fichier. Synchronisés / À publier → lien profond galerie. */}
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
        <Stat label={t('exif.taggedAssets')} value={publish.taggedAssets} />
        <Stat
          label={t('exif.inSync')}
          value={publish.inSync}
          icon="sync"
          onSelect={() => onSelectPublishState('inSync')}
          actionLabel={t('exif.filterInSync')}
        />
        <Stat
          label={t('exif.outOfSync')}
          value={publish.outOfSync}
          icon="warn"
          onSelect={() => onSelectPublishState('outOfSync')}
          actionLabel={t('exif.filterOutOfSync')}
        />
      </div>

      <FolderScope
        idPrefix="publish"
        value={folder}
        options={folderOptions}
        onChange={onFolderChange}
      />

      <div className="flex flex-col gap-2">
        <Toggle
          checked={keepOriginal}
          onChange={setKeepOriginal}
          label={t('exif.keepOriginal')}
          hint={t('exif.keepOriginalHint')}
        />
        {/* Décoché : écriture SANS sauvegarde (irréversible) — avertissement explicite. */}
        {!keepOriginal && <InlineWarning text={t('exif.noBackupWarning')} />}
        <Toggle
          checked={dryRun}
          onChange={setDryRun}
          label={t('exif.dryRun')}
          hint={t('exif.dryRunHint')}
        />
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          disabled={busy}
          onClick={() => void submit()}
          className="inline-flex items-center gap-2 rounded-md bg-brand px-4 py-2 text-sm font-medium text-brand-fg disabled:opacity-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
        >
          <TagIcon className="h-5 w-5" aria-hidden="true" />
          {busy ? t('exif.queuing') : dryRun ? t('exif.previewPublish') : t('exif.publish')}
        </button>
        {queuedJobId && (
          <p role="status" className="inline-flex items-center gap-1 text-sm text-fg">
            <CheckCircleIcon className="h-5 w-5 shrink-0" aria-hidden="true" />
            {interpolate(t('exif.jobQueued'), { id: queuedJobId.slice(0, 8) })}
          </p>
        )}
        {error !== null && (
          <p role="alert" className="inline-flex items-center gap-1 text-sm text-danger">
            <ExclamationTriangleIcon className="h-5 w-5 shrink-0" aria-hidden="true" />
            {error === 'writeDisabled' ? t('exif.errorWriteDisabled') : t('exif.jobFailed')}
          </p>
        )}
      </div>
    </section>
  );
}
